/**
 * Parse and serialize Work Experience section content for structured card editing (Kickresume-style).
 */

export interface WorkExperienceEntry {
  id: string;
  company?: string;
  jobTitle?: string;
  city?: string;
  country?: string;
  fromDate?: string;
  toDate?: string;
  currentlyHere?: boolean;
  description?: string;
}

const WORK_JSON_PREFIX = '___WORK_JSON___';

function generateId(): string {
  return `work-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Match date range: 04/2019 – 10/2023, 2019 - 2022, 01/2020 - Present, till date, etc. */
const DATE_RANGE_REGEX = /(\d{1,2}\/\d{4}|\d{4})\s*[–\-—]\s*(\d{1,2}\/\d{4}|\d{4}|Present|Current|till\s+date|now)/i;

function parseHeaderLine(headerLine: string): { jobTitle?: string; company?: string; city?: string; fromDate?: string; toDate?: string; currentlyHere?: boolean } {
  const fromHeader: { jobTitle?: string; company?: string; city?: string; fromDate?: string; toDate?: string; currentlyHere?: boolean } = {};
  const dateMatch = headerLine.match(DATE_RANGE_REGEX);
  if (dateMatch) {
    fromHeader.fromDate = dateMatch[1];
    const to = dateMatch[2].toLowerCase();
    fromHeader.currentlyHere = to === 'present' || to === 'current' || to === 'till date' || to === 'now';
    fromHeader.toDate = fromHeader.currentlyHere ? '' : dateMatch[2];
  }
  const beforeDate = dateMatch ? headerLine.slice(0, dateMatch.index).trim() : headerLine.trim();
  if (!beforeDate) return fromHeader;
  // "COMPANY - Location" or "COMPANY - LOCATION/IN 01/2016" → company = first part, rest = city/location
  const dashSplit = beforeDate.split(/\s+-\s+/);
  if (dashSplit.length >= 2) {
    const first = dashSplit[0].trim();
    const rest = dashSplit.slice(1).join(' - ').trim();
    if (first && !/^\d{1,2}\/\d{4}$/.test(first)) {
      fromHeader.company = first;
      if (rest && !/^\d{1,2}\/\d{4}$/.test(rest)) fromHeader.city = rest;
    }
  } else {
    const parts = beforeDate.split(/\s*[·|]\s*/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      fromHeader.jobTitle = parts[0];
      fromHeader.company = parts[1];
    } else if (parts.length === 1 && parts[0]) {
      fromHeader.jobTitle = parts[0];
    }
  }
  return fromHeader;
}

/**
 * Split plain text into multiple work experience blocks.
 * 1) Split by double newline into blocks; or
 * 2) If one block, split by lines that look like a new job (contain date range).
 */
function splitPlainTextIntoBlocks(content: string): string[] {
  const normalized = content.replace(/\r\n/g, '\n').trim();
  const blocks = normalized.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  if (blocks.length > 1) return blocks;
  if (blocks.length === 0) return [];
  const single = blocks[0];
  const lines = single.split(/\n/).map((l) => l.trim());
  const headerIndices: number[] = [];
  lines.forEach((line, i) => {
    if (DATE_RANGE_REGEX.test(line)) headerIndices.push(i);
  });
  if (headerIndices.length <= 1) return [single];
  const result: string[] = [];
  if (headerIndices[0] > 0) {
    result.push(lines.slice(0, headerIndices[0]).join('\n'));
  }
  for (let i = 0; i < headerIndices.length; i++) {
    const start = headerIndices[i];
    const end = i + 1 < headerIndices.length ? headerIndices[i + 1] : lines.length;
    result.push(lines.slice(start, end).join('\n'));
  }
  return result;
}

export function parseWorkExperienceContent(content: string): WorkExperienceEntry[] {
  if (!content || !content.trim()) return [{ id: generateId() }];
  const trimmed = content.trim();
  if (trimmed.startsWith(WORK_JSON_PREFIX)) {
    try {
      const json = trimmed.slice(WORK_JSON_PREFIX.length).trim();
      const arr = JSON.parse(json) as unknown;
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map((item: any) => ({
          id: typeof item.id === 'string' ? item.id : generateId(),
          company: typeof item.company === 'string' ? item.company : '',
          jobTitle: typeof item.jobTitle === 'string' ? item.jobTitle : '',
          city: typeof item.city === 'string' ? item.city : '',
          country: typeof item.country === 'string' ? item.country : '',
          fromDate: typeof item.fromDate === 'string' ? item.fromDate : '',
          toDate: typeof item.toDate === 'string' ? item.toDate : '',
          currentlyHere: !!item.currentlyHere,
          description: typeof item.description === 'string' ? item.description : '',
        }));
      }
    } catch {
      // fall through
    }
  }
  const blocks = splitPlainTextIntoBlocks(trimmed);
  if (blocks.length === 0) return [{ id: generateId() }];
  return blocks.map((block) => {
    const lines = block.split(/\n/).map((l) => l.trim()).filter(Boolean);
    const headerLine = lines[0] || '';
    const description = lines.slice(1).join('\n').trim() || '';
    const parsed = parseHeaderLine(headerLine);
    return {
      id: generateId(),
      jobTitle: parsed.jobTitle ?? '',
      company: parsed.company ?? '',
      city: parsed.city ?? '',
      fromDate: parsed.fromDate ?? '',
      toDate: parsed.toDate ?? '',
      currentlyHere: !!parsed.currentlyHere,
      description,
    };
  });
}

export function serializeWorkExperienceContent(entries: WorkExperienceEntry[]): string {
  const list = entries.map((e) => ({
    id: e.id,
    company: e.company ?? '',
    jobTitle: e.jobTitle ?? '',
    city: e.city ?? '',
    country: e.country ?? '',
    fromDate: e.fromDate ?? '',
    toDate: e.toDate ?? '',
    currentlyHere: !!e.currentlyHere,
    description: e.description ?? '',
  }));
  return WORK_JSON_PREFIX + JSON.stringify(list);
}

export function isWorkExperienceJsonContent(content: string): boolean {
  return typeof content === 'string' && content.trim().startsWith(WORK_JSON_PREFIX);
}

/** Compute duration string from fromDate/toDate or currentlyHere (e.g. "4 years 7 months", "9 years"). */
function formatDuration(fromDate?: string, toDate?: string, currentlyHere?: boolean): string {
  if (!fromDate) return '';
  const parseMonthYear = (s: string): { y: number; m: number } | null => {
    const mm = s.match(/^(\d{1,2})\/(\d{4})$/);
    if (mm) return { m: parseInt(mm[1], 10), y: parseInt(mm[2], 10) };
    const yy = s.match(/^(\d{4})$/);
    if (yy) return { m: 0, y: parseInt(yy[1], 10) };
    return null;
  };
  const from = parseMonthYear(fromDate.trim());
  if (!from) return '';
  const to = currentlyHere
    ? (() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() + 1 }; })()
    : toDate?.trim() ? parseMonthYear(toDate) : null;
  if (!to) return '';
  let months = (to.y - from.y) * 12 + (to.m - from.m);
  if (months < 0) return '';
  const years = Math.floor(months / 12);
  const monthsPart = months % 12;
  if (years > 0 && monthsPart > 0) return `${years} year${years !== 1 ? 's' : ''} & ${monthsPart} month${monthsPart !== 1 ? 's' : ''}`;
  if (years > 0) return `${years} year${years !== 1 ? 's' : ''}`;
  return `${monthsPart} month${monthsPart !== 1 ? 's' : ''}`;
}

export function workExperienceEntriesToDisplayText(entries: WorkExperienceEntry[]): string {
  return entries
    .map((e) => {
      const headerParts: string[] = [];
      if (e.company) headerParts.push(e.company);
      if (e.jobTitle) headerParts.push(e.jobTitle);
      const loc = [e.city, e.country].filter(Boolean).join(', ');
      if (loc) headerParts.push(loc);
      const header = headerParts.join(' · ');
      const dateParts: string[] = [];
      if (e.fromDate) dateParts.push(e.fromDate);
      if (e.currentlyHere) dateParts.push('Present');
      else if (e.toDate) dateParts.push(e.toDate);
      const dateLine = dateParts.join(' – ');
      const duration = formatDuration(e.fromDate, e.toDate, e.currentlyHere);
      const durationStr = duration ? ` [${duration}]` : '';
      const line1 = header || dateLine;
      const line2 = dateLine ? dateLine + durationStr : '';
      const desc = (e.description || '').trim();
      if (line2) return line1 + '\n' + line2 + (desc ? '\n' + desc : '');
      return desc ? line1 + '\n' + desc : line1;
    })
    .join('\n\n');
}

export function getWorkExperienceDisplayContent(content: string): string {
  if (isWorkExperienceJsonContent(content)) {
    return workExperienceEntriesToDisplayText(parseWorkExperienceContent(content));
  }
  return content;
}
